'use client';

import React, { useMemo, useRef, useState } from 'react';
import { useDeviceType } from '@/hooks/useDeviceType';
import { InfoPopover } from '@/components/ui/InfoPopover';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { cn } from '@/lib/cn';
import type { Listing } from '@repo/api';
import { useAnalytics } from '@/hooks/useAnalytics';

function formatMoney(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '';
  return `$${amount.toLocaleString('en-US')}`;
}

function formatLeaseTermMonths(leaseTermMonths: number | null | undefined): string {
  if (!leaseTermMonths) return '';
  if (leaseTermMonths % 12 === 0) {
    const years = leaseTermMonths / 12;
    return `${years} year${years === 1 ? '' : 's'}`;
  }
  return `${leaseTermMonths} months`;
}

function PriceDetailsContent({
  grossRent,
  promotion,
  leaseTermMonths,
  netRent,
}: {
  grossRent: number;
  promotion: string | null;
  leaseTermMonths: number | null;
  netRent: number;
}) {
  const items = useMemo(
    () => [
      { value: formatMoney(grossRent), label: 'Base rent' },
      { value: promotion, label: 'Promotion' },
      { value: formatLeaseTermMonths(leaseTermMonths), label: 'Lease term' },
    ],
    [grossRent, leaseTermMonths, promotion],
  );

  return (
    <div className="flex flex-col gap-6 pb-4 pt-8">
      {promotion && leaseTermMonths && grossRent && netRent && netRent < grossRent && (
        <div className="flex items-center justify-center gap-2 px-6">
          {items.map(({ value, label }, idx) => (
            <React.Fragment key={label}>
              {idx > 0 ? <div className="h-8 w-px shrink-0 bg-tertiary" aria-hidden /> : null}
              <div className="flex flex-1 flex-col items-center justify-center gap-1 text-center">
                <div className="text-body-2 text-primary">{value}</div>
                <div className="text-caption text-secondary">{label}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="px-6">
        <div className="rounded-2xl bg-secondary p-4 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="flex flex-col items-center gap-1">
              <div className="text-body-1-high text-success">{formatMoney(netRent)}</div>
              <div className="text-body-2-high text-primary">Net effective rent</div>
            </div>
            <div className="text-caption text-secondary">
              This price is based on current promotions. The amount you pay each month may be
              different. Verify details with an Openigloo agent.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PriceDetailsTrigger({
  grossRent,
  promotion,
  leaseTermMonths,
  netRent,
  analyticsScreen,
  analyticsType,
  className,
  children,
}: {
  grossRent: number;
  promotion: string | null;
  leaseTermMonths: number | null;
  netRent: number;
  analyticsScreen: 'listing_card' | 'unit';
  analyticsType: 'two_prices' | 'one_price';
  className?: string;
  children: React.ReactNode;
}) {
  const { isMobile } = useDeviceType();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const analytics = useAnalytics();

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    analytics.track('price_details', { screen: analyticsScreen, type: analyticsType });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={handleOpen}
        className={cn('underline underline-offset-3', className)}
      >
        {children}
      </button>

      {isMobile ? (
        <BottomSheet
          open={open}
          onOpenChange={setOpen}
          label="Price details"
          onClose={handleClose}
          bodyClassName="!px-0 !py-0"
        >
          <PriceDetailsContent
            grossRent={grossRent}
            promotion={promotion}
            leaseTermMonths={leaseTermMonths}
            netRent={netRent}
          />
        </BottomSheet>
      ) : (
        <InfoPopover
          open={open}
          onOpenChange={setOpen}
          onClose={handleClose}
          label="Price details"
          anchorRef={anchorRef}
          placement="below"
          align="right"
          offsetY={8}
          bodyClassName="!px-0 !py-0"
          contentClassName="rounded-4xl pb-2 max-w-[calc(100vw-32px)]"
        >
          <PriceDetailsContent
            grossRent={grossRent}
            promotion={promotion}
            leaseTermMonths={leaseTermMonths}
            netRent={netRent}
          />
        </InfoPopover>
      )}
    </>
  );
}

export function ListingPromotionPrice({
  listing,
  screen,
  className,
  grossClassName,
  netClassName,
}: {
  listing: Listing;
  screen: 'listing_card' | 'unit';
  className?: string;
  grossClassName?: string;
  netClassName?: string;
}) {
  const fallbackRent = listing?.previousListingRent ?? null;

  const grossRent = listing.activeListing?.grossRent ?? null;
  const leaseTermMonths = listing.activeListing?.leaseTermMonths ?? null;
  const promotion = listing.activeListing?.promotion ?? null;
  const netRent = listing.activeListing?.netRent ?? null;

  const displayRent = grossRent ?? fallbackRent;
  const displayPrice = displayRent ? formatMoney(displayRent) : '';

  const isFutureListing = Boolean(listing.activeListing?.isFutureListing);
  const showPromotion =
    Boolean(promotion && leaseTermMonths && grossRent && netRent) &&
    !isFutureListing &&
    (netRent as number) < (grossRent as number);

  const netPrice = netRent ? formatMoney(netRent) : '';

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {showPromotion ? (
        <>
          <span className={cn(grossClassName, 'text-secondary line-through')}>{displayPrice}</span>
          <PriceDetailsTrigger
            grossRent={grossRent as number}
            promotion={promotion}
            leaseTermMonths={leaseTermMonths}
            netRent={netRent as number}
            analyticsScreen={screen}
            analyticsType="two_prices"
            className={cn(netClassName)}
          >
            {netPrice}
          </PriceDetailsTrigger>
        </>
      ) : netRent ? (
        <PriceDetailsTrigger
          grossRent={grossRent as number}
          promotion={promotion}
          leaseTermMonths={leaseTermMonths}
          netRent={netRent as number}
          analyticsScreen={screen}
          analyticsType="one_price"
          className={cn(netClassName)}
        >
          {netPrice}
        </PriceDetailsTrigger>
      ) : (
        <span className={cn(netClassName)}>{displayPrice}</span>
      )}
    </div>
  );
}
